import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Form/Input";
import { resetPassword, validateToken } from "../../services";
import { toast } from "react-toastify";
import { Loader } from "../../components/Loader";
import { Navigate, useLocation, useNavigate } from "react-router";
import { getAuthToken } from "../../helpers";
import { AuthShell } from "../../components/Auth/AuthShell";

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [form, setForm] = useState({ new_password: "", token: "" });
    const [confirm_password, setConfirmPassword] = useState("");

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await resetPassword(form);
            toast("Password reset successful", { type: "success" });
            navigate("/login");
        } catch (err: any) {
            toast("Error: " + (err?.error || "Failed to reset password"), { position: "bottom-right", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const verifyToken = async () => {
            const token = params.get("token");
            if (!token) {
                setVerifying(false);
                setIsValid(false);
                return;
            }
            try {
                const res = await validateToken(String(token));
                if (res?.data?.valid) {
                    setForm((prev) => ({ ...prev, token: String(token) }));
                    setIsValid(true);
                } else {
                    setIsValid(false);
                }
            } catch (err) {
                setIsValid(false);
            } finally {
                setVerifying(false);
            }
        };
        verifyToken();
        // eslint-disable-next-line
    }, []);

    if (getAuthToken()) return <Navigate to="/" />;

    const passwordMatch = confirm_password === form.new_password;
    const isFormValid = form.new_password && passwordMatch && form.new_password.length >= 8;

    return (
        <AuthShell
            heading="New Password"
            subheading={verifying ? "Verifying your link..." : isValid ? "Create a strong new password" : "This password reset link is invalid"}
        >
            {verifying ? (
                <div className="flex flex-col items-center py-10">
                    <Loader color="text-primary-900" />
                    <p className="text-sm text-slate-500 mt-4">Security check in progress...</p>
                </div>
            ) : !isValid ? (
                <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6 border border-red-100">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-red-600">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                        </svg>
                    </div>
                    <p className="font-body text-slate-600 mb-8 leading-relaxed">
                        The link you followed is either invalid or has expired for security reasons. Please request a new reset link.
                    </p>
                    <button
                        onClick={() => navigate("/forgot-password")}
                        className="w-full py-3.5 rounded-xl font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors shadow-md"
                    >
                        Request New Link
                    </button>
                </div>
            ) : (
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex flex-col gap-6">
                    <Input
                        type="password"
                        label="New password"
                        placeholder="At least 8 characters"
                        action={(e) => setForm({ ...form, new_password: e.target.value })}
                        isRequired
                    />
                    <div>
                        <Input
                            type="password"
                            label="Confirm password"
                            placeholder="Repeat your new password"
                            action={(e) => setConfirmPassword(e.target.value)}
                            mb={false}
                            isRequired
                        />
                        {confirm_password && !passwordMatch && (
                            <p className="text-xs text-danger mt-2 font-bold flex items-center gap-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd" />
                                </svg>
                                Passwords don't match
                            </p>
                        )}
                        {form.new_password && form.new_password.length < 8 && (
                            <p className="text-xs text-amber-600 mt-2 font-bold flex items-center gap-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                                </svg>
                                Use at least 8 characters
                            </p>
                        )}
                    </div>
                    <Button
                        title={loading ? "Updating..." : "Set New Password"}
                        className="mt-2 w-full !py-3.5 shadow-md shadow-primary-900/20"
                        size="lg"
                        disabled={!isFormValid || loading}
                        icon={loading ? <Loader /> : undefined}
                        action={() => { }}
                        type="submit"
                    />
                </form>
            )}
        </AuthShell>
    );
};

export default ResetPassword;
